/**
 * @owner @Appboy/messaging-experience
 */
import type { CanvasError } from "@canvas/errors";

import type { CampaignError } from "@campaigns/errors";

import type { MessagePrioritizationOptInLimitType, MessagePrioritizationOptInLimits } from "./resolveOptInLimits";

/**
 * Resolves the numeric cap for a scheduled or triggered opt-in bucket.
 *
 * @param limitType - Which message prioritization opt-in bucket applies.
 * @param limits - Resolved scheduled/triggered caps.
 * @returns The numeric limit for the requested bucket.
 */
export const resolveMessagePrioritizationOptInLimitForType = (
  limitType: MessagePrioritizationOptInLimitType,
  limits: MessagePrioritizationOptInLimits
): number => {
  if (limitType === "scheduled") {
    return limits.scheduled;
  }

  return limits.triggered;
};

/**
 * Adds the resolved numeric cap for a canvas limit-reached error's bucket so error-tray
 * copy can show both the bucket (`limitType`) and its number (`limit`).
 *
 * @param error - Canvas error emitted by legacy KO validation.
 * @param limits - Resolved scheduled/triggered caps.
 * @returns Error with numeric `limit` args when applicable.
 */
export const enrichCanvasMessagePrioritizationLimitError = (
  error: CanvasError,
  limits: MessagePrioritizationOptInLimits
): CanvasError => {
  if (error.type !== "messagePrioritizationCanvasLimitReached") {
    return error;
  }

  return {
    type: error.type,
    args: {
      limitType: error.args.limitType,
      limit: resolveMessagePrioritizationOptInLimitForType(error.args.limitType, limits),
    },
  };
};

/**
 * Adds the resolved numeric cap for a campaign limit-reached error's bucket so error-tray
 * copy can show both the bucket (`limitType`) and its number (`limit`).
 *
 * @param error - Campaign error emitted by legacy KO validation.
 * @param limits - Resolved scheduled/triggered caps.
 * @returns Error with numeric `limit` args when applicable.
 */
export const enrichCampaignMessagePrioritizationLimitError = (
  error: CampaignError,
  limits: MessagePrioritizationOptInLimits
): CampaignError => {
  if (error.type !== "messagePrioritizationCampaignLimitReached") {
    return error;
  }

  return {
    type: error.type,
    args: {
      limitType: error.args.limitType,
      limit: resolveMessagePrioritizationOptInLimitForType(error.args.limitType, limits),
    },
  };
};
